/**
 * SEQ Logging utility for frontend
 * Sends structured logs directly to SEQ server
 */

// Get SEQ configuration - frontend only needs to know if SEQ logging is enabled
// The backend handles all SEQ configuration and forwarding
const getSeqConfig = () => {
  // Check if window.RUNTIME_CONFIG exists (from runtime-config.js)
  if (typeof window !== 'undefined' && window.RUNTIME_CONFIG) {
    return {
      enabled: window.RUNTIME_CONFIG.seqEnabled === true || window.RUNTIME_CONFIG.seqEnabled === 'true',
      logLevel: window.RUNTIME_CONFIG.seqLogLevel || 'INFO'
    };
  }
  
  // Fallback to environment variables
  return {
    enabled: process.env.REACT_APP_SEQ_ENABLED === 'true' || process.env.REACT_APP_SEQ_ENABLED === true,
    logLevel: process.env.REACT_APP_SEQ_LOG_LEVEL || 'INFO'
  };
};

// Cache config but allow refresh
let seqConfigCache = null;
const getCachedConfig = () => {
  if (!seqConfigCache) {
    seqConfigCache = getSeqConfig();
  }
  return seqConfigCache;
};

// Allow refreshing config (useful if RUNTIME_CONFIG loads later)
const refreshConfig = () => {
  seqConfigCache = getSeqConfig();
  return seqConfigCache;
};

// Log levels mapping
const LOG_LEVELS = {
  DEBUG: 0,
  INFO: 1,
  WARNING: 2,
  ERROR: 3,
  CRITICAL: 4
};

const getCurrentLogLevel = () => {
  const config = getCachedConfig();
  return LOG_LEVELS[config.logLevel.toUpperCase()] || LOG_LEVELS.INFO;
};

// Batch logs for efficient sending
let logBuffer = [];
const MAX_BUFFER_SIZE = 50;
const FLUSH_INTERVAL = 5000; // 5 seconds
let flushTimer = null;
let errorCount = 0;
let lastError = null;

// Initialize flush timer
const initFlushTimer = () => {
  if (flushTimer) {
    clearInterval(flushTimer);
  }
  flushTimer = setInterval(() => {
    flushLogs();
  }, FLUSH_INTERVAL);
};

// Format timestamp in ISO format for SEQ
const formatTimestamp = () => {
  return new Date().toISOString();
};

// Convert log level to SEQ format
const getSeqLevel = (level) => {
  const levelMap = {
    'DEBUG': 'Debug',
    'INFO': 'Information',
    'WARNING': 'Warning',
    'ERROR': 'Error',
    'CRITICAL': 'Critical'
  };
  return levelMap[level.toUpperCase()] || 'Information';
};

// Check if log level should be sent
const shouldSendLog = (level) => {
  const levelValue = LOG_LEVELS[level.toUpperCase()] || LOG_LEVELS.INFO;
  return levelValue >= getCurrentLogLevel();
};

// Send logs to SEQ via backend proxy
const sendLogsToSeq = async (logs) => {
  const config = getCachedConfig();
  
  // Frontend only needs to check if SEQ logging is enabled
  // Backend handles all SEQ server configuration
  if (!config.enabled || logs.length === 0) {
    return;
  }

  // Send logs to backend endpoint which will forward to SEQ
  // Get backend URL from runtime config or environment
  const getBackendUrl = () => {
    if (typeof window !== 'undefined' && window.RUNTIME_CONFIG && window.RUNTIME_CONFIG.backendUrl) {
      return window.RUNTIME_CONFIG.backendUrl;
    }
    return process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
  };

  const backendUrl = getBackendUrl();
  const endpoint = `${backendUrl}/api/logs/frontend/seq`;

  try {
    // Format logs for backend SEQ endpoint
    const formattedLogs = logs.map(log => ({
      timestamp: log.timestamp,
      level: log.level,
      message: log.message,
      module: log.module || 'Frontend',
      logger: log.logger || 'Salinator-Frontend',
      properties: log.properties || {}
    }));

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ logs: formattedLogs }),
      credentials: 'include' // Include cookies for authentication
    });

    if (response.ok) {
      // Success - reset error count
      errorCount = 0;
      lastError = null;
    } else {
      // Log error but don't spam - use original console to avoid recursion
      errorCount++;
      const errorText = await response.text().catch(() => 'Unable to read error');
      const origConsole = getOriginalConsole();
      if (origConsole && origConsole.error && errorCount <= 3) {
        origConsole.error(`[SEQ] Backend returned error ${response.status}: ${errorText.substring(0, 200)}`);
      }
      lastError = `HTTP ${response.status}`;
    }
  } catch (error) {
    errorCount++;
    const origConsole = getOriginalConsole();
    if (origConsole && origConsole.error && errorCount <= 3) {
      origConsole.error(`[SEQ] Request failed: ${error.name}: ${error.message}`);
    }
    lastError = `${error.name}: ${error.message.substring(0, 100)}`;
  }
};

// Flush buffered logs
const flushLogs = async () => {
  if (logBuffer.length === 0) {
    return;
  }

  const logsToSend = [...logBuffer];
  logBuffer = [];
  
  await sendLogsToSeq(logsToSend);
};

// Store original console methods to avoid recursion
let originalConsole = null;
const getOriginalConsole = () => {
  if (!originalConsole && typeof window !== 'undefined') {
    // Try to get original console from window if available
    originalConsole = window.__ORIGINAL_CONSOLE__ || {
      log: (...args) => {
        // Use a try-catch to avoid recursion
        try {
          const log = window.console.log;
          if (log && log !== console.log) {
            log.apply(window.console, args);
          }
        } catch (e) {
          // Silent fail
        }
      }
    };
  }
  return originalConsole;
};

// Add log to buffer
const addLog = (level, message, module = 'Frontend', properties = {}) => {
  const config = getCachedConfig();
  
  if (!config.enabled) {
    return;
  }

  if (!shouldSendLog(level)) {
    return;
  }

  logBuffer.push({
    timestamp: formatTimestamp(),
    level: level.toUpperCase(),
    message: typeof message === 'string' ? message : JSON.stringify(message),
    logger: 'Salinator-Frontend',
    module: module,
    properties: properties
  });

  // Flush immediately for errors
  if (level.toUpperCase() === 'ERROR' || level.toUpperCase() === 'CRITICAL') {
    flushLogs();
  }

  // Flush if buffer is full
  if (logBuffer.length >= MAX_BUFFER_SIZE) {
    flushLogs();
  }
};

// Initialize SEQ logger
const initSeqLogger = () => {
  // Refresh config to ensure we have latest values
  const config = refreshConfig();
  
  // Frontend only needs to check if SEQ logging is enabled
  // Backend handles all SEQ server configuration
  if (!config.enabled) {
    return false;
  }

  // Initialize flush timer
  initFlushTimer();

  // Flush logs before page unload
  if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', () => {
      const config = getCachedConfig();
      if (logBuffer.length > 0 && config.enabled) {
        // Get backend URL for sending logs
        const getBackendUrl = () => {
          if (typeof window !== 'undefined' && window.RUNTIME_CONFIG && window.RUNTIME_CONFIG.backendUrl) {
            return window.RUNTIME_CONFIG.backendUrl;
          }
          return process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
        };
        
        const backendUrl = getBackendUrl();
        const endpoint = `${backendUrl}/api/logs/frontend/seq`;

        // Format logs as JSON for backend endpoint
        const logsJson = JSON.stringify({ logs: logBuffer.map(log => ({
          timestamp: log.timestamp,
          level: log.level,
          message: log.message,
          module: log.module || 'Frontend',
          logger: log.logger || 'Salinator-Frontend',
          properties: log.properties || {}
        })) });
        
        const blob = new Blob([logsJson], { type: 'application/json' });
        navigator.sendBeacon(endpoint, blob);
      }
    });
  }

  // Send test log
  addLog('INFO', 'SEQ logging enabled - frontend logger initialized', 'SEQ-Logger', {
    logLevel: config.logLevel
  });

  return true;
};

// Test function to manually send a log
const testSeqLogging = async () => {
  const origConsole = getOriginalConsole();
  const logFn = origConsole?.log || (() => {}); // Use original console or no-op
  const errorFn = origConsole?.error || (() => {});
  
  logFn('=== SEQ Logging Test ===');
  const config = getCachedConfig();
  logFn('Current config:', config);
  logFn('Buffer size:', logBuffer.length);
  logFn('Error count:', errorCount);
  logFn('Last error:', lastError);
  logFn('Is enabled:', config.enabled);
  
  if (!config.enabled) {
    errorFn('ERROR: SEQ logging is not enabled!');
    errorFn('Set REACT_APP_SEQ_ENABLED=true or configure seqEnabled in runtime-config.js');
    return;
  }
  
  // Generate unique test ID
  const testId = `SEQ-MANUAL-TEST-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  
  logFn(`Sending test log with ID: ${testId}`);
  
  // Force add a test log
  addLog('INFO', `Manual SEQ test log from browser console - ID: ${testId}`, 'SEQ-Test', { 
    testId: testId,
    timestamp: new Date().toISOString(),
    test: true,
    manualTest: true
  });
  
  // Force flush
  await flushLogs();
  
  logFn('=== Test Complete ===');
  logFn(`Please check your SEQ instance for a log containing: "${testId}"`);
  logFn('Look for:');
  logFn('  - Application="Salinator-Frontend"');
  logFn('  - SourceContext="SEQ-Test"');
  logFn('  - testId="' + testId + '"');
  logFn('If you see this log in SEQ, logging is working correctly!');
};

// Export SEQ logger functions
export const seqLogger = {
  debug: (message, module, properties) => addLog('DEBUG', message, module, properties),
  info: (message, module, properties) => addLog('INFO', message, module, properties),
  warn: (message, module, properties) => addLog('WARNING', message, module, properties),
  error: (message, module, properties) => addLog('ERROR', message, module, properties),
  critical: (message, module, properties) => addLog('CRITICAL', message, module, properties),
  flush: flushLogs,
  init: initSeqLogger,
  isEnabled: () => {
    const config = getCachedConfig();
    // Frontend only checks if SEQ logging is enabled, backend handles SEQ server config
    return config.enabled;
  },
  getConfig: () => {
    const config = getCachedConfig();
    return { ...config };
  },
  refreshConfig: refreshConfig,
  getErrorCount: () => errorCount,
  getLastError: () => lastError,
  test: testSeqLogging
};

// Make test function available globally for debugging
if (typeof window !== 'undefined') {
  window.testSeqLogging = testSeqLogging;
  window.seqLogger = seqLogger;
}

export default seqLogger;


